<script setup lang="ts">
/**
 * CoreSelectField (single-select, no-typing)
 *
 * Renders a Headless UI Listbox wrapped by CoreField to keep label/description/
 * error behavior consistent with other core form controls.
 *
 * Why Listbox instead of a native <select>: the native control paints its
 * option list with an OS-owned popup we cannot style or reliably position (it
 * detaches from the control inside device-emulation / transformed ancestors,
 * and opens a full-screen modal picker on mobile that breaks immersion). The
 * Listbox renders its options as in-DOM markup positioned by our own CSS, so it
 * stays on-theme and correctly anchored on every platform while Headless UI
 * still handles roving focus, type-ahead, Esc, and click-outside for us.
 *
 * - `modelValue` stores a `CoreSelectItem | null`.
 * - `items` can be strings or objects; object items require `itemTitle` to
 *   resolve the display label.
 */
import { computed, inject, onBeforeUnmount, onMounted, ref, toRef } from 'vue'
import { Listbox, ListboxButton, ListboxOptions, ListboxOption } from '@headlessui/vue'
import ChevronDownIcon from 'vue-material-design-icons/ChevronDown.vue'
import CheckIcon from 'vue-material-design-icons/Check.vue'
import CoreField from '../CoreField.vue'
import type { CoreErrorMessages, CoreFieldDensity, CoreFieldVariant, CoreRule, CoreSelectItem, CoreSelectObject } from '../types'
import { coreFormKey } from '../keys'
import { getCoreControlClasses } from '../controlClasses'
import { normalizeErrorMessages, useRulesValidation } from '../useRulesValidation'
import { useStableId } from '../useStableId'

type NormalizedItem = {
  key: string
  label: string
  value: string
  raw: CoreSelectItem
}

const props = withDefaults(
  defineProps<{
    modelValue: CoreSelectItem | null
    items: CoreSelectItem[]
    itemTitle?: string
    placeholder?: string
    label?: string
    description?: string
    errorMessages?: CoreErrorMessages
    rules?: CoreRule<CoreSelectItem | null>[]
    variant?: CoreFieldVariant
    density?: CoreFieldDensity
    disabled?: boolean
    readonly?: boolean
    required?: boolean
    name?: string
    id?: string
    hideDetails?: 'auto' | 'always' | 'never'
  }>(),
  {
    itemTitle: undefined,
    placeholder: undefined,
    label: undefined,
    description: undefined,
    errorMessages: undefined,
    rules: undefined,
    variant: 'outlined',
    density: 'default',
    disabled: false,
    readonly: false,
    required: false,
    name: undefined,
    id: undefined,
    hideDetails: 'auto',
  },
)

const emit = defineEmits<{
  (e: 'update:modelValue', value: CoreSelectItem | null): void
}>()

const form = inject(coreFormKey, null)
const touched = ref(false)

const stableId = useStableId({
  id: toRef(props, 'id'),
  name: toRef(props, 'name'),
  prefix: 'core-field',
})

const normalizedOptions = computed<NormalizedItem[]>(() => {
  return props.items.map((item, index) => {
    if (typeof item === 'string') {
      return { key: `str-${index}-${item}`, label: item, value: `str-${index}`, raw: item }
    }

    if (typeof props.itemTitle !== 'string') {
      throw new Error('CoreSelectField: itemTitle is required when items include objects.')
    }

    const obj = item as CoreSelectObject
    const labelValue = obj[props.itemTitle]
    if (typeof labelValue !== 'string') {
      throw new Error('CoreSelectField: itemTitle must reference a string property on each object option.')
    }

    return { key: `obj-${index}-${labelValue}`, label: labelValue, value: `obj-${index}`, raw: item }
  })
})

const matchesValue = (a: CoreSelectItem, b: CoreSelectItem) => {
  if (typeof a === 'string' && typeof b === 'string') return a === b
  if (typeof a === 'object' && typeof b === 'object') {
    if (typeof props.itemTitle !== 'string') return false
    const aLabel = (a as CoreSelectObject)[props.itemTitle]
    const bLabel = (b as CoreSelectObject)[props.itemTitle]
    return aLabel === bLabel && typeof aLabel === 'string'
  }
  return false
}

// Headless UI uses this to decide which option is selected. Handle the null
// (nothing selected / placeholder) case explicitly since matchesValue only
// compares real items.
const compareItems = (a: CoreSelectItem | null, b: CoreSelectItem | null) => {
  if (a == null || b == null) return a === b
  return matchesValue(a, b)
}

const selectedLabel = computed(() => {
  if (props.modelValue == null) return ''
  const opt = normalizedOptions.value.find((item) => matchesValue(item.raw, props.modelValue as CoreSelectItem))
  return opt?.label ?? ''
})

const externalErrors = computed(() => normalizeErrorMessages(props.errorMessages))
const hasExternalErrors = computed(() => externalErrors.value.length > 0)
const canShowErrors = computed(() => touched.value || form?.submitted.value)

const { ruleErrors, validateRules, resetRules } = useRulesValidation({
  value: computed({
    get: () => props.modelValue,
    set: (next) => emit('update:modelValue', next),
  }),
  rules: toRef(props, 'rules'),
})

const activeErrors = computed<string[]>(() => {
  if (!canShowErrors.value) return []
  if (hasExternalErrors.value) return externalErrors.value
  return ruleErrors.value
})

const invalid = computed(() => activeErrors.value.length > 0)
const controlClass = computed(() =>
  getCoreControlClasses({ variant: props.variant, density: props.density, invalid: invalid.value }),
)

const validateCurrent = async () => {
  touched.value = true
  if (props.disabled) {
    resetRules()
    return []
  }
  if (hasExternalErrors.value) return externalErrors.value
  return await validateRules()
}

const resetValidation = () => {
  touched.value = false
  resetRules()
}

let unregister: (() => void) | null = null
onMounted(() => {
  if (!form) return
  const key = props.name || stableId.value
  unregister = form.registerField({
    key,
    validate: validateCurrent,
    resetValidation,
  })
})

onBeforeUnmount(() => unregister?.())

const onBlur = async () => {
  await validateCurrent()
}

const onSelect = async (value: CoreSelectItem | null) => {
  emit('update:modelValue', value ?? null)

  if (canShowErrors.value && !hasExternalErrors.value) {
    await validateRules()
  }
}
</script>

<template>
  <CoreField
    :id="stableId"
    :name="name"
    :label="label"
    :description="description"
    :error-messages="activeErrors"
    :required="required"
    :disabled="disabled"
    :readonly="readonly"
    :variant="variant"
    :density="density"
    :hide-details="hideDetails"
  >
    <template #default="{ id: controlId, describedBy, invalid: slotInvalid, disabled: slotDisabled, readonly: slotReadonly, required: slotRequired }">
      <Listbox
        as="div"
        class="relative"
        :model-value="modelValue"
        :by="compareItems"
        :disabled="slotDisabled || slotReadonly"
        @update:model-value="onSelect"
      >
        <ListboxButton
          :id="controlId"
          :class="[controlClass, 'flex items-center justify-between gap-2 text-left cursor-pointer']"
          :aria-invalid="slotInvalid ? 'true' : undefined"
          :aria-describedby="describedBy"
          @blur="onBlur"
        >
          <span
            class="min-w-0 truncate"
            :class="{ 'text-[color:var(--theme-field-placeholder)]': !selectedLabel }"
          >
            {{ selectedLabel || placeholder || 'Select…' }}
          </span>
          <ChevronDownIcon :size="18" class="shrink-0 text-on-surface-variant" aria-hidden="true" />
        </ListboxButton>

        <Transition
          enter-active-class="transition duration-100 ease-out"
          enter-from-class="opacity-0 -translate-y-1"
          enter-to-class="opacity-100 translate-y-0"
          leave-active-class="transition duration-75 ease-in"
          leave-from-class="opacity-100 translate-y-0"
          leave-to-class="opacity-0 -translate-y-1"
        >
          <ListboxOptions
            class="absolute z-20 mt-1 max-h-64 w-full min-w-0 overflow-auto rounded-ui border border-border bg-surface-overlay py-1 text-on-surface shadow-floating focus:outline-none"
          >
            <ListboxOption
              v-if="!slotRequired"
              :value="null"
              as="template"
              v-slot="{ active, selected }"
            >
              <li
                :class="[
                  'flex cursor-pointer items-center justify-between gap-2 px-3 py-2 text-sm transition-default duration-fast',
                  active ? 'bg-primary-subtle text-on-surface' : 'text-text-secondary',
                ]"
              >
                <span class="min-w-0 truncate text-[color:var(--theme-field-placeholder)]">
                  {{ placeholder || 'None' }}
                </span>
                <CheckIcon v-if="selected" :size="16" class="shrink-0" aria-hidden="true" />
              </li>
            </ListboxOption>

            <ListboxOption
              v-for="option in normalizedOptions"
              :key="option.key"
              :value="option.raw"
              as="template"
              v-slot="{ active, selected }"
            >
              <li
                :class="[
                  'flex cursor-pointer items-center justify-between gap-2 px-3 py-2 text-sm transition-default duration-fast',
                  active ? 'bg-primary-subtle text-on-surface' : 'text-text-secondary',
                ]"
              >
                <span class="min-w-0 truncate">{{ option.label }}</span>
                <CheckIcon v-if="selected" :size="16" class="shrink-0" aria-hidden="true" />
              </li>
            </ListboxOption>
          </ListboxOptions>
        </Transition>
      </Listbox>
    </template>
  </CoreField>
</template>
